import React, { useState, useEffect } from "react";

/**
 * Renders a country flag from the external flagsapi.com service.
 *
 * flagsapi.com occasionally fails to respond (network flakiness, rate
 * limiting, momentary CDN failure), which previously left a broken flag
 * image with no retry or fallback. This component retries once with a
 * cache-buster, then falls back to the country's emoji flag (from
 * countries.json's `flag` field) so something always renders.
 */
const CountryFlag = ({ code, emoji, alt, className, style }) => {
    const [attempt, setAttempt] = useState(0);
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        setAttempt(0);
        setFailed(false);
    }, [code]);

    if (!code) return null;

    if (failed) {
        return emoji ? (
            <span
                className={className}
                style={{ fontSize: "20px", lineHeight: 1, ...style }}
                role="img"
                aria-label={alt || ""}
            >
                {emoji}
            </span>
        ) : null;
    }

    const upperCode = String(code).toUpperCase();
    const src = `https://flagsapi.com/${upperCode}/flat/24.png${
        attempt ? `?retry=${attempt}` : ""
    }`;

    return (
        <img
            key={attempt}
            src={src}
            alt={alt || ""}
            className={className}
            style={style}
            onError={() => {
                if (attempt < 1) setAttempt((prev) => prev + 1);
                else setFailed(true);
            }}
        />
    );
};

export default CountryFlag;
